import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { TarlV3Jsonapi } from '®shared/tarl-v3-jsonapi';

import {
  UserAnnotation,
  UserReadingIntention,
  UserStudyNote,
} from './user-annotation';

@Injectable()
export class UserAnnotationsService {
  constructor(private tarlV3Jsonapi: TarlV3Jsonapi) {}

  /**
   * Get annotations for a user on a given list.
   *
   * @param {string} listId - The ID of the list for annotation lookup.
   * @param {string} userId - The ID of the user for annotation lookup.
   * @return {Observable<UserAnnotation>} - Annotations for the user on the given list
   **/
  getUserAnnotations(
    listId: string,
    userId: string,
  ): Observable<UserAnnotation<any>[]> {
    const path = this.tarlV3Jsonapi.path(
      '/lists/{listId}/users/{userId}/annotations',
      {
        listId,
        userId,
      },
    );
    return this.tarlV3Jsonapi.getResources<UserAnnotation<any>>(path);
  }

  /**
   * Delete a reading intention against a resource.
   *
   * @param {string} userId - The ID of the user owning the annotation
   * @param {string} resourceId - The ID of the resource being annotated
   * @return {Observable<HttpResponse<null>>} - Empty response body on successful deletion.
   **/
  deleteReadingIntention(
    userId: string,
    resourceId: string,
  ): Observable<HttpResponse<null>> {
    const path = this.tarlV3Jsonapi.path(
      '/users/{userId}/reading_intentions/{resourceId}',
      { resourceId, userId },
    );
    return this.tarlV3Jsonapi.delete(path);
  }

  /**
   * Update a reading intention against a resource.
   *
   * @param {string} userId - The ID of the user owning the annotation
   * @param {string} resourceId - The ID of the resource being annotated
   * @return {Observable<UserReadingIntention>} - The updated reading intention
   **/
  updateReadingIntentionStatus(
    userId: string,
    resourceId: string,
    status: string,
  ): Observable<UserReadingIntention> {
    const readingIntention = this.makeReadingIntentionRequestBody(
      userId,
      resourceId,
      status,
    );
    const path = this.tarlV3Jsonapi.path(
      '/users/{userId}/reading_intentions/{resourceId}',
      { resourceId, userId },
    );
    return this.tarlV3Jsonapi.patchResource<UserReadingIntention>(
      path,
      readingIntention,
    );
  }

  /**
   * Create a reading intention for a given user, resource, with a given status.
   *
   * @param {string} userId - The ID of the user owning the annotation
   * @param {string} resourceId - The ID of the resource being annotated
   * @param {string} status - The target status of the reading intention
   * @return {UserReadingIntention} - a reading intention object
   **/
  makeReadingIntentionRequestBody(
    userId: string,
    resourceId: string,
    status: string,
  ): UserReadingIntention {
    return Object.assign(
      new UserReadingIntention(),
      this.annotationRequestBody<UserReadingIntention>(userId, resourceId),
      {
        status,
        type: 'reading_intentions',
      },
    );
  }

  /**
   * Delete a study note against a resource.
   *
   * @param {string} userId - The ID of the user owning the annotation
   * @param {string} resourceId - The ID of the resource being annotated
   * @return {Observable<HttpResponse<null>>} - Empty response body on successful deletion.
   **/
  deleteStudyNote(
    userId: string,
    resourceId: string,
  ): Observable<HttpResponse<null>> {
    const path = this.tarlV3Jsonapi.path(
      '/users/{userId}/study_notes/{resourceId}',
      {
        resourceId,
        userId,
      },
    );
    return this.tarlV3Jsonapi.delete(path);
  }

  /**
   * Update a study note against a resource.
   *
   * @param {string} userId - The ID of the user owning the annotation
   * @param {string} resourceId - The ID of the resource being annotated
   * @param {string} note - The content of the note
   * @return {Observable<UserStudyNote>} - The updated study note
   **/
  updateStudyNoteContent(
    userId: string,
    resourceId: string,
    note: string,
  ): Observable<UserStudyNote> {
    const studyNote = this.makeStudyNoteRequestBody(userId, resourceId, note);
    const path = this.tarlV3Jsonapi.path(
      '/users/{userId}/study_notes/{resourceId}',
      {
        resourceId,
        userId,
      },
    );
    return this.tarlV3Jsonapi.patchResource<UserStudyNote>(path, studyNote);
  }

  /**
   * Create a study note for a given user, resource, and note content.
   *
   * @param {string} userId - The ID of the user owning the annotation
   * @param {string} resourceId - The ID of the resource being annotated
   * @param {string} note - The content of the note
   * @return {UserStudyNote} - a study note object
   **/
  makeStudyNoteRequestBody(
    userId: string,
    resourceId: string,
    note: string,
  ): UserStudyNote {
    return Object.assign(
      new UserStudyNote(),
      this.annotationRequestBody<UserStudyNote>(userId, resourceId),
      {
        note,
        type: 'study_notes',
      },
    );
  }

  /**
   * Create a natural JSON representation of an annotation, suitable for JSON:API serialisation.
   *
   * @param {string} userId - The ID of the user owning the annotation
   * @param {string} resourceId - The ID of the resource being annotated
   * @return {any} - An annotation JSON body, for JSON:API serialisation.
   **/
  annotationRequestBody<T extends UserAnnotation<T>>(
    userId: string,
    resourceId: string,
  ): Partial<T> {
    return {
      id: this.tarlV3Jsonapi.compoundId(userId, resourceId),
      resource: { id: resourceId, type: 'resources' },
      user: { id: userId, type: 'users' },
    } as T;
  }
}
